import ComplaintStatusPill from '@/components/Dashboard/Order/components/ComplaintStatusPill.vue';
import Select from '@/components/Shared/Select.vue';
import Tooltip from '@/components/Shared/Tooltip.vue';
import {
  can,
  cleanName,
  CustimooOrderFlowStatuses,
  formatDate,
  formatShortDate,
  getComplaintsArray,
  logError,
  mergeComplaintsOntoOrderDetail,
  setPageTitle,
  timeAgo,
} from '@/composables';
import { useVueQuery } from '@/composables/useVueQuery';
import { http } from '@/interceptors/axiosRequestInterceptor';
import {
  DashboardItem,
  DashboardRequest,
  FactoryProduct,
  OrderAdminUser,
  OrderDetail,
  OrderDetailItem,
  Urgency,
} from '@/models';
import { useMainStore } from '@/store';
import { getImpersonation } from '@/composables/impersonation';
import { useDashboardStore } from '@/store/dashboard';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon, KeyIcon } from '@heroicons/vue/24/solid';
import { createPopper } from '@popperjs/core';
import { useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, h, nextTick, ref, VNode, watch } from 'vue';
import { formatTimestampWithBrowserTimezone } from '@/composables/useTimeZoneFormatDate';
import { formatInvoiceStatusLabel, invoiceCompositeStatusPillClass } from '@/utils/invoiceCompositeStatusDisplay';
import orderHoldIcon from '@/assets/img/images/stop.svg';
import { useRoute, useRouter } from 'vue-router';
import { showToast } from './swal';
import {
  makeReorderMessage as sharedMakeReorderMessage,
  type ReorderFactoryProduct,
} from '@/utils/reorderMessage';
declare global {
  interface Window {
    virtualReference?: {
      getBoundingClientRect: () => DOMRect;
    };
  }
}
interface OrderPage {
  items: DashboardItem[];
  total: number;
  nextPage?: number;
}

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  renderer?: (value: any, row: any) => string | VNode | number;
}


export const useDashboard = () => {
  const store = useMainStore();
  const dashboardStore = useDashboardStore();
  const appURL = import.meta.env.VITE_VUE2_APP_URL;

  const {
    dashboardData,
    dashboardTabs,
    permissionLoaded,
    orderAdmins,
    dashboardQuickFilters,
    loading,
    sidebarOpen,
  } = storeToRefs(store);

  const { modeTabs, currentMode, orderList } = storeToRefs(dashboardStore);

  const datatable = ref(null);

  const queryClient = useQueryClient();

  const invalidateOrderActivityLogQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['order-activity-logs'] });
    await queryClient.invalidateQueries({ queryKey: ['order-activity-log-detail'] });
  };

  const showDashboardActions = ref<boolean>(false);

  const currentTab = ref('all_items');
  const paneDefaultTab = ref<string>(localStorage.getItem('activeDetailsTab') ?? 'timeline');
  const selectedFilter = ref<string | null>(null);
  const createManualOrder = ref<boolean>(false);
  const assignFactoryModal = ref<boolean>(false);
  const selectedOrderDetails = ref<OrderDetail>(null);
  const isSidepaneLoading = ref<boolean>(false);
  const priceBreakdownModal = ref<boolean>(false);
  const selectedOrderItem = ref<DashboardItem>(null);
  const selectedUrgentOrderFilter = ref<string | null>(null);
  const internalNoteModal = ref(false);
  const internalNoteRow = ref(null);
  const taskSearchQuery = ref(null);
  const isDisabled = ref(false);

  let defParams: DashboardRequest = {
    items_for: currentTab.value ?? 'all_items',
    sort_by: 'order_no',
    sort_order: 'desc',
    page: 1,
    search: '',
    per_page: 10,
    new_dashboard: true
  };

  const dashboardActionsRef = ref<any>(null);

  const defParamsString = JSON.stringify({ dashboardRequest: defParams });
  const currentDashboardModel: Record<string, DashboardRequest> = JSON.parse(
    localStorage.getItem('dashboardRequest') ?? defParamsString
  );
  const savedRowIds = ref<{ order_id?: number; id?: number } | null>(JSON.parse(localStorage.getItem('currentRow') ?? 'null'));
  const currentActiveRow = ref<DashboardItem | undefined | null>(null);
  const openDetails = ref<boolean>(JSON.parse(localStorage.getItem('openDetails')) ?? false)
  const fetchOrder = ref<boolean>(true);
  const current_filters = ref<DashboardRequest>(currentDashboardModel.dashboardRequest);
 // near the top of useDashboard()
const { user: currentUser } = storeToRefs(store);
// Check for order_no in URL query parameters
const route = useRoute();
const router = useRouter();
let popperInstance: any = null;

// 1) Read URL params once
const urlOrderNo = route.query.order_no ?? null;
const urlOrderId = route.query.order_id ?? null;
const urlOrderItemId = route.query.order_item_id ?? null;

// 2) If deep-link params exist, configure filters + saved row BEFORE creating the query
if (urlOrderId && urlOrderItemId) {
  // Force filters to search by this order_no on first load
  current_filters.value = {
    ...current_filters.value,
    items_for: currentTab.value ?? 'all_items',
    sort_by: 'order_no',
    sort_order: 'desc',
    page: 1,
    search: String(urlOrderNo),
    new_dashboard: true,
    order_id: Number(urlOrderId),
  };

  // Reset quick filters so they don't conflict
  dashboardQuickFilters.value = {};

  // Prepare detail pane state so that once data comes, we can open that row
  savedRowIds.value = {
    order_id: Number(urlOrderId),
    id: Number(urlOrderItemId),
  };
  openDetails.value = true;
  localStorage.setItem('currentRow', JSON.stringify(savedRowIds.value));
  localStorage.setItem('openDetails', JSON.stringify(true));
  localStorage.setItem('activeDetailsTab', 'timeline');

  // 3) Clear the URL so refresh/back doesn't re-trigger this deep-link
  router.replace({ query: {} });
}

// 4) Normal query ? no router calls or URL logic inside
const { data, error, isFetching, isPending } = useVueQuery<OrderPage, Error>(
  'orders',
  async () => {

    const params: DashboardRequest = {
      ...defParams,
      ...current_filters.value,
      page: current_filters.value.page,
    };

    await store.getDashboardData(params, dashboardQuickFilters.value);
    const currentData = dashboardData.value?.items;

    // Restore active row from saved IDs if available.
    //
    // Bug 20628 (Zendesk #8494) — this block runs inside the orders query fn, so it re-executes on
    // EVERY orders refetch, including the one `fetchOnSave` triggers via `invalidateQueries(['orders'])`
    // after a save. Because `savedRowIds` was seeded once from `localStorage['currentRow']` and never
    // consumed, a save could re-target `currentRow` / `selectedOrderDetails` to whatever order was last
    // handled on that machine — which remounted the open Price Breakdown modal onto that other order
    // (and, on `master`, left it there). Three guards keep it to its intended job, "reopen the pane I
    // had open when the page loaded":
    //   1. never re-target while an order-scoped modal is open,
    //   2. consume `savedRowIds` once used, so a later refetch cannot resurrect it,
    //   3. `handleCellClick` / `openDashboardActions` clear it as soon as the user picks a row
    //      themselves — at that point the restore intent is already obsolete.
    if (
      savedRowIds.value &&
      savedRowIds.value.order_id &&
      savedRowIds.value.id &&
      fetchOrder.value &&
      openDetails.value &&
      !priceBreakdownModal.value &&
      currentData?.data
    ) {
      const restoreIds = savedRowIds.value;
      // Consume up-front: `handleCellClick` below awaits, and any refetch racing that await must not
      // see the ids as still pending.
      savedRowIds.value = null;

      const savedRow = currentData.data.find(
        (row: DashboardItem) =>
          row.id === restoreIds.id &&
          row.order?.id === restoreIds.order_id
      );

      if (savedRow) {
        currentActiveRow.value = savedRow;
        await handleCellClick(
          {
            row: savedRow,
            column: {
              key: 'id',
              label: 'id',
            },
          },
          { fromRestore: true },
        );
      } else {
        try {
          const orderData = await store.getOrderDetail(
            restoreIds.order_id,
            restoreIds.id
          );
          if (orderData) {
            selectedOrderDetails.value = orderData;
            setPageTitle(`#${orderData.order_no}`);
              const foundRow = currentData.data.find(
              (row: DashboardItem) => row.id === restoreIds.id
              );
              selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(
                orderData,
                foundRow?.order ?? null
              );
              setPageTitle(`#${orderData.order_no}`);
              if (foundRow) {
              currentRow.value = foundRow;
              currentActiveRow.value = foundRow;
            }
          }
        } catch (error) {
          logError('Failed to restore order details:', error);
        }
      }
    }

    return {
      items: currentData?.data ?? [],
      total: currentData?.total ?? 0,
    };
  },
  {
    queryKey: [
      'orders',
      currentTab,
      dashboardQuickFilters,
      defParams.sort_by,
      defParams.sort_order,
      defParams.search,
      current_filters.value.page,
    ],
    refetchOnWindowFocus: false,
  }
);

  const tabs = [
    { id: 'timeline', label: 'Timeline' },
    { id: 'detail', label: 'Detail' },
    { id: 'files', label: 'Files' },
    { id: 'activity_logs', label: 'Activity Logs' },
    // AB#21202 — Ask ELSA. A tab rather than a button in the action row: the
    // order detail is already the right-hand pane, so an overlay would cover
    // the timeline every claim in the analysis cites, and a button that only
    // switches to a tab is a duplicate control in a row AB#21204 just cut from
    // twenty-one to nine.
    //
    // `variant` and `separated` make it look like what it is. Shipped first as
    // a plain tab, which was wrong: identical shape to the four beside it meant
    // it read as a fifth view of the order rather than as someone you ask, and
    // when it was not selected nothing distinguished it at all.
    { id: 'ask_elsa', label: 'Ask ELSA', variant: 'agent', separated: true },
  ];
  const filteredOrderDetailTabs = computed(() => {
    return tabs.filter((tab) => {
      if (tab.id === 'files') return can('sharepoint-files');
      if (tab.id === 'activity_logs') return can('view-activity-logs');
      // ask_elsa is deliberately ungated for now, matching its routes. A
      // permission here would need its route mapping to exist in the
      // production database in the same release, and 104 gated routes already
      // have no mapping anywhere in the repo (AB#21176). Who should see this
      // tab is a product question; when it is answered, the permission and the
      // mapping land together.
      return true;
    });
  });
  const canAssignOrderAdmin = computed(() => can('assign-order-administrator'));
  const currentRow = ref<DashboardItem | null>(null);
  const previousSelectedRow = ref<DashboardItem | null>(null);

  const selectedRow: ComputedRef<DashboardItem | null> = computed(() => {
    if (currentActiveRow.value && fetchOrder.value && datatable.value && !previousSelectedRow.value && openDetails.value) {
      fetchOrder.value = false;
      scrollToCurrentRow();
      return currentActiveRow.value
    }
    else if (openDetails.value === false && datatable.value && !currentRow.value && currentActiveRow.value) {
      previousSelectedRow.value = JSON.parse(JSON.stringify(currentActiveRow.value));
      currentActiveRow.value = null;
      scrollToCurrentRow();
      return previousSelectedRow.value;
    }
    else if (currentRow.value) {
      return currentRow.value;
    } else if (previousSelectedRow.value) {
      return previousSelectedRow.value;
    } else {
      return null;
    }
  });

  const canViewShippingAddress = computed(() => can('order-listing-shipping-address-column'));
  const canViewMerchant = computed(() => can('order-listing-merchant-column'));
  const canViewPaymentStatus = computed(() => can('view-payment-status'));
  const canViewFactory = computed(() => can('order-listing-factory-column'));

  const filteredModeTabs = computed(() => {
    return modeTabs.value.filter((tab) => {
      if (tab.id === 'calendar') {
        return can('can-see-calendar');
      }
      if (tab.id === 'my-tasks') {
        return can('view-my-tasks');
      }
      return true;
    });
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      case 'delayed':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };
  // AB#19827 (Zendesk#7813) - My Orders tab only: when a customer rejects a sample the order
  // item stays at "submitted_for_customer_review" while the individual factory product becomes
  // "customer_rejected". Detect that so the order admin can spot it at a glance. Display-only.
  const REJECTED_SAMPLE_STATUS = 'customer_rejected';
  const CUSTOMER_REVIEW_STATUS = 'submitted_for_customer_review';
  const isMyOrdersRejected = (row: DashboardItem): boolean => {
    if (currentTab.value !== 'my_orders') return false;
    if (row?.status !== CUSTOMER_REVIEW_STATUS) return false;
    let fps: any = row?.factory_products;
    if (typeof fps === 'string') {
      try { fps = JSON.parse(fps); } catch (e) { fps = []; }
    }
    return Array.isArray(fps) && fps.some((fp: any) => fp?.status === REJECTED_SAMPLE_STATUS);
  };
  const orderOptions = computed(() =>
    orderList.value.map((order) => ({
      ...order,
      label: order.customer_reference_no
        ? `${order.order_no} (${order.customer_reference_no})`
        : order.order_no,
    }))
  );
  const handlePriceBreakDownModalCloseEvent = async (fetch_order_items = false) => {
    priceBreakdownModal.value = false;
    // `handleOrderUpdated` already runs `getOrderDetail` + `fetchOnSave(true)` ? do not call `fetchOnSave` twice.
    if (fetch_order_items) {
      await handleOrderUpdated();
    }
  };
  const onOrderSearch = async (query: string, loading: (b: boolean) => void) => {
    loading(true);
    await dashboardStore.searchOrders(query);
    loading(false);
  };

  const onModeTabChange = (tabId: string) => {
    dashboardStore.setCurrentMode(tabId);

    if (tabId === 'calendar' && !dashboardStore.calendarInitialized) {
      dashboardStore.initializeCalendar();
    }
  };

  const handleViewChanged = (payload: {
    view: string;
    selected_date: string;
    selected_holiday_country_code: string;
  }) => {
    dashboardStore.setCurrentView(payload.view);
    dashboardStore.setSelectedDate(new Date(payload.selected_date));
    dashboardStore.setSelectedHolidayCountryCode(payload.selected_holiday_country_code);
  };

  const onCalendarFilterChange = (filters: any) => {
    if (currentMode.value === 'calendar') {
      if (
        filters &&
        typeof filters === 'object' &&
        ('items_for' in filters || 'quick_filters' in filters)
      ) {
        if (!filters.quick_filters || Object.keys(filters.quick_filters).length === 0) {
          dashboardStore.setCalendarFilters(null);
        } else {
          dashboardStore.setCalendarFilters(filters);
        }
        if (filters.items_for === 'my_assigned_orders') {
          dashboardStore.setCalendarFilters({
            ...filters,
            my_assigned_orders: true,
          });
        }
      } else {
        dashboardStore.setCalendarFilters(null);
      }
    } else {
      selectedFilter.value = typeof filters === 'string' ? filters : null;
    }
  };

  const factoryModalClose = () => {
    assignFactoryModal.value = false;
    selectedOrderItem.value = null;
  };

  const tableData = computed(() => {
    return data.value?.items ?? [];
  });

  const totalItems = computed(() => {
    return data.value?.total ?? 0;
  });


  const fetchRecords = async (data: any): Promise<void> => {
    defParams = {
      items_for: currentTab.value ?? 'all_items',
      sort_by: data?.sortColumn ?? 'order_no',
      sort_order: data?.sortOrder ?? data?.sort_order,
      page: data?.page ?? 1,
      per_page: data?.pageSize ?? data?.per_page,
      search: data?.searchQuery ?? '',
      new_dashboard: true
    };

    current_filters.value = {
      ...current_filters.value,
      ...defParams,
    };

    localStorage.setItem(
      'dashboardRequest',
      JSON.stringify({ dashboardRequest: current_filters.value })
    );

    await queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  const onTabChange = async (tab: string): Promise<void> => {
    if (tab === 'calendar') {
      currentTab.value = tab;
    } else {
      currentTab.value = tab;
      defParams = {
        ...defParams,
        items_for: currentTab.value,
        sort_by: 'order_no',
        sort_order: 'desc',
        page: 1,
      };
      let localFilterInfo = localStorage.getItem('dashboardRequest');
      current_filters.value = {
        ...JSON.parse(localFilterInfo ?? defParamsString).dashboardRequest,
        ...defParams,
      };
    }
  };

  const fetchOnSave = async (fetch: boolean, skipOrderDetailRefetch = false) => {
    if (!fetch) return;

    let localFilterInfo: Record<string, DashboardRequest> = JSON.parse(
      localStorage.getItem('dashboardRequest') ?? defParamsString
    );
    current_filters.value = localFilterInfo.dashboardRequest;

    const row = currentRow.value;
    let detailItemFromRefetch: OrderDetailItem | undefined;
    let detailForRow: OrderDetail | undefined;

    if (row && !skipOrderDetailRefetch) {
      try {
        const orderData = await store.getOrderDetail(row.order.id, row.id);
        detailForRow = orderData;
        selectedOrderDetails.value = orderData;
        detailItemFromRefetch = orderData?.items?.find((i) => i.id === row.id);
        if (detailItemFromRefetch && currentRow.value) {
          const refetchedPriceInfo = detailItemFromRefetch.price_info as unknown as
            | DashboardItem['price_info']
            | undefined;
          const refetchedTotalPrice = (
            detailItemFromRefetch.price_info as { total_price?: number } | undefined
          )?.total_price;
          currentRow.value = {
            ...currentRow.value,
            price_info: refetchedPriceInfo ?? currentRow.value.price_info,
            currency: detailItemFromRefetch.currency ?? currentRow.value.currency,
            billing_company_id:
              detailItemFromRefetch.billing_company_id ?? currentRow.value.billing_company_id,
            factory_products:
              detailItemFromRefetch.factory_products ?? currentRow.value.factory_products,
            item_invoices:
              (detailItemFromRefetch as any).item_invoices ??
              (detailItemFromRefetch as any).itemInvoices ??
              currentRow.value.item_invoices,
            invoice_composite_status:
              detailItemFromRefetch.invoice_composite_status ??
              currentRow.value.invoice_composite_status,
            order_item_total_price:
              Number(refetchedTotalPrice) || currentRow.value.order_item_total_price,
          };
        }
      } catch (error) {
        logError('Error refetching order details after price breakdown save:', error);
      }
    }


    await queryClient.invalidateQueries({ queryKey: ['orders'] });

    // Bug 20628 (Zendesk #8494) — the refetch above re-runs the orders query fn, which may re-target
    // `selectedOrderDetails` at another order. `currentRow` is healed below, but nothing restored the
    // detail payload, so the pane and the `:key`-ed Price Breakdown modal stayed on the wrong order.
    // Re-assert the payload we already fetched for `row` — no extra request.
    if (detailForRow && selectedOrderDetails.value?.id !== detailForRow.id) {
      selectedOrderDetails.value = detailForRow;
      setPageTitle(`#${detailForRow.order_no}`);
    }

    if (row && tableData.value && Array.isArray(tableData.value)) {
      const updated = tableData.value.find((r) => r.id === row.id);
      if (updated) {
        const refetchedPriceInfo = detailItemFromRefetch?.price_info as unknown as
          | DashboardItem['price_info']
          | undefined;
        const refetchedTotalPrice = (
          detailItemFromRefetch?.price_info as { total_price?: number } | undefined
        )?.total_price;
        currentRow.value = {
          ...updated,
          price_info: refetchedPriceInfo ?? updated.price_info,
          currency: detailItemFromRefetch?.currency ?? updated.currency,
          billing_company_id:
            detailItemFromRefetch?.billing_company_id ?? updated.billing_company_id,
          factory_products:
            detailItemFromRefetch?.factory_products ?? updated.factory_products,
          item_invoices:
            (detailItemFromRefetch as any)?.item_invoices ??
            (detailItemFromRefetch as any)?.itemInvoices ??
            updated.item_invoices,
          invoice_composite_status:
            detailItemFromRefetch?.invoice_composite_status ?? updated.invoice_composite_status,
          order_item_total_price:
            refetchedTotalPrice != null
              ? Number(refetchedTotalPrice)
              : updated.order_item_total_price,
        };
        localStorage.setItem(
          'currentRow',
          JSON.stringify({ order_id: currentRow.value.order.id, id: currentRow.value.id }),
        );
      }
    }

    scrollToCurrentRow();
    await invalidateOrderActivityLogQueries();
  };

  const loginCustomer = async (customer_id: number): Promise<void> => {
    loading.value = true;
    await http
      .get(`/customer/information/${customer_id}`)
      .then((res) => {
        let token = res.data.access_token;
        if (res.data.user.company_url) {
          const url = res.data.user.company_url + '/#/';
          // Tell the builder who is impersonating so its banner can name the
          // admin. If this session is itself an impersonated one, credit the
          // original admin from the impersonation context.
          const mainStore = useMainStore();
          const impBy = getImpersonation()?.by ?? {
            name: mainStore.user?.name ?? '',
            email: mainStore.user?.email ?? '',
            role: mainStore.role?.name ?? '',
          };
          const impParams =
            `&imp_by_name=${encodeURIComponent(impBy.name || '')}` +
            `&imp_by_email=${encodeURIComponent(impBy.email || '')}` +
            `&imp_by_role=${encodeURIComponent(impBy.role || '')}`;
          window.open(`${url}admin/login?token=${token}${impParams}`);
        }
      })
      .catch((err) => {
        logError(err);
      });
    loading.value = false;
  };

  const getUrgencyClass = (urgency: Urgency, id: number): string => {
    switch (urgency) {
      case 'VIP':
        return 'bg-green-300 !text-[#686666] !p-0 ';
      case 'Urgent':
        return 'order_cancel !text-[#686666] !p-0';
      case 'Extra Attention':
        return 'bg-blue-300 !text-[#686666] !p-0';
      default:
        return `!p-0 ${id && selectedRow.value && selectedRow.value.id === id ? 'bg-gray-300' : ''}`;
    }
  };

  const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

  const getDaySuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const parseDashboardCalendarDate = (dateValue: string): Date | null => {
    const trimmed = String(dateValue ?? '').trim();
    if (!trimmed) return null;

    // Keep date-only strings stable across different local timezones.
    if (DATE_ONLY_REGEX.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      return Number.isNaN(localDate.getTime()) ? null : localDate;
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDashboardEstShippingDate = (dateValue: string, shortFormat: boolean): string => {
    const date = parseDashboardCalendarDate(dateValue);
    if (!date) return dateValue;

    const day = date.getDate();
    if (shortFormat) {
      const shortDay = String(day).padStart(2, '0');
      const shortMonth = date.toLocaleString('en-US', { month: 'short' });
      const shortYear = String(date.getFullYear()).slice(-2);
      return `${shortDay} ${shortMonth} ${shortYear}`;
    }

    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day}${getDaySuffix(day)} ${month} ${year}`;
  };

  const visibleColumns = computed(() => {
    const baseColumns = [
      {
        key: currentTab.value === 'quote_requested_items' ? 'order_no' : 'quote_no',
        label: currentTab.value === 'quote_requested_items' ? `Quote No` : `Order No`,
        sortable: true,
        sortKey: 'order_no',
        cellClass: (row: DashboardItem) => {
          if (row.order?.customer_shop_id) {
            // Shop-in-Shop orders get colored
            return '!bg-[#ffb350] !text-white text-center !p-0';
          }
          return currentTab.value !== 'quote_requested_items' && can('show-urgency-filters')
            ? getUrgencyClass((row as DashboardItem).order?.urgency, row?.id)
            : '!p-0 ' + (selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '');
        },
        renderer: (val: any, row: DashboardItem) => {
          // Item-level symbol is written by price breakdown updates; order-level is set at order create / some legacy paths.
          const rawSymbol = row.order_type_symbol ?? row.order?.order_type_symbol;
          const urgency = row.order?.urgency;
          const isManualOrder = row.order?.additional_fields?.is_manual_order;
          const orderNumber = row.order
          ? `${isManualOrder ? 'M_#' : '#'}${row.item_no ? row.item_no : (row.order?.order_no || '')}`
          : '';
          const cleanedSymbol = rawSymbol?.replace(/i/g, '');
          const allowedSymbols = ['S', 'R'];
          const showSymbol = rawSymbol && allowedSymbols.includes(cleanedSymbol);
          const padding = '0.75rem';




          const children = [
            orderNumber,
            showSymbol &&
            h(
              'span',
              {
                style: {
                  color: 'red',
                  fontWeight: 'bold',
                  marginLeft: '4px',
                  fontSize: '1rem',
                },
              },
              cleanedSymbol
            ),
          ];
          if (row.internal_comment) {
            children.push(
              h(
                Tooltip,
                {
                  placement: 'top',
                  content: JSON.parse(row.internal_comment).comment,
                },
                {
                  trigger: () =>
                    h(
                      'button',
                      {
                        class: 'ml-4 cursor-pointer align-middle',
                        style:
                          'display:inline-flex;vertical-align:middle;background:none;border:none;padding:0;',
                        onClick: (e) => {
                          e.stopPropagation();
                          openInternalNoteModal(row);
                        },
                        title: 'View/Add Internal Note',
                      },
                      [
                        h(ChatBubbleBottomCenterTextIcon, {
                          class: 'size-5 text-[#2600D8]',
                        }),
                      ]
                    ),
                }
              )
            );
          }

          // Show on-hold icon with tooltip containing latest reason (if order is on_hold)
          if (row.status === 'on_hold') {
            let holdReasonText = 'N/A';

            try {
              const raw = row.on_hold_reason;
              const parsed = typeof raw === 'string' && raw.trim()
                ? JSON.parse(raw)
                : raw;

              if (Array.isArray(parsed) && parsed.length > 0) {
                const last = parsed[parsed.length - 1];

                // New format: { status, reason, timestamp, user_id }
                if (last && typeof last === 'object') {
                  holdReasonText = last.reason || 'N/A';
                }
              }
            } catch (err) {
              holdReasonText = row?.on_hold_reason || 'N/A';
            }

            children.push(
              h(
                Tooltip,
                {
                  placement: 'top',
                  contentClass: 'whitespace-normal max-w-sm !p-4',
                },
                {
                  trigger: () =>
                    h('img', {
                      src: orderHoldIcon,
                      class: 'ml-2 flex-shrink-0', // Added flex-shrink-0
                      style: {
                        width: '20px',      // Explicit width
                        height: '20px',     // Explicit height
                        minWidth: '20px',   // Prevent shrinking
                        minHeight: '20px',  // Prevent shrinking
                        objectFit: 'contain' // Maintain aspect ratio
                      }
                    }),
                  default: () =>
                    h('div', {  class: 'flex flex-col gap-2 max-w-sm w-full z-[10000]' }, [
                      h('div', {
                        class: 'font-bold text-[18px] text-gray-800 pb-2 z-[10000] border-b border-gray-200 mb-2 mt-2 break-words',
                      }, 'Order on hold'),
                      h('div', { class: 'text-[14px] leading-relaxed max-w-sm z-[10000]' }, [
                        h('div', { class: 'font-semibold text-gray-700 mb-2 z-[10000]' }, 'Reasoning:'),
                        h('div', { class: 'text-gray-600 pl-2 whitespace-normal break-words max-w-full z-[10000]' }, holdReasonText),
                      ]),
                    ])
                }
              )
            );
          }

          const cellChilds = [

            h(
              'span',
              {
                class: 'text-sm',
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
              },
              children
            ),
            row.reorder_order_ids && h(
              'span',
              {
                class: 'text-xs bg-primary p-1 rounded-[20px] text-center text-white',
              },
              'Reorder'
            ),
          ].filter(Boolean)
          const cellContent = h(
            'div',
            {
              class: 'w-full h-full flex flex-col gap-2',
              style: {
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                padding: padding,
              },
            },
            cellChilds
          );

          if (urgency && can('show-urgency-filters')) {
            return h(
              Tooltip,
              {
                placement: 'right',
                content: `${urgency ? urgency.replace(/_/g, ' ') : 'normal'} Order`,
                widthClass: 'w-full',
              },
              {
                trigger: () => cellContent,
              }
            );
          } else {
            return cellContent;
          }
        },
      },
      {
        key: '',
        label: openDetails.value ? 'By' : 'Customer',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const { customer_name, customer_email } = row.order;

          let mainContent;
          const customerName = cleanName(customer_name);
          if (openDetails.value || customerName.length > 30) {
            // Show initials with tooltip
            let displayVal = '';
            let tooltipVal = '';

            if (customerName?.trim()?.length) {
              tooltipVal = customerName;
              // Get initials (first letter of each word, max 2)
              const initials = customerName
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              displayVal = `${initials}..`;
            } else if (customer_email?.trim()?.length) {
              tooltipVal = customer_email;
              displayVal = `${customer_email.slice(0, 2).toUpperCase()}...`;
            }

            mainContent = h(
              Tooltip,
              {
                placement: 'top',
                content: tooltipVal,
              },
              {
                trigger: () => h('span', { class: 'whitespace-nowrap font-semibold' }, displayVal),
              }
            );
          } else {
            // Show full name or email
            mainContent = h(
              'span',
              { class: 'whitespace-nowrap font-semibold' },
              customerName?.trim()?.length ? customerName : customer_email
            );
          }

          // "Login as Customer" button with icon and tooltip
          const loginButton = h(
            Tooltip,
            {
              placement: 'right',
              content: 'Login as Customer',
            },
            {
              trigger: () =>
                h(
                  'button',
                  {
                    class:
                      'bg-primary text-white text-sm font-medium px-2 p-1 rounded-[4px] cursor-pointer flex gap-1 items-center',
                    onClick: () => {
                      loginCustomer(row.order.customer_id);
                    },
                  },
                  [
                    h(KeyIcon, {
                      class: 'w-4 h-4 text-white',
                    }),
                  ]
                ),
            }
          );

          return h('span', { class: 'flex gap-1 items-center justify-between' }, [
            mainContent,
            loginButton,
          ]);
        },
      },
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        sortKey: 'status_updated_at',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const dateValue = row.order.created_at ?? Date.now().toString();
          if (openDetails.value) {
            return formatShortDate(dateValue);
          }
          return formatDate(dateValue);
        },
      },
      {
        key: '',
        label: 'Est. Shipping Date',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          if (!row.est_shipping_date) return 'N/A';
          const dateVal = Array.isArray(row.est_shipping_date)
            ? row.est_shipping_date[row.est_shipping_date.length - 1]
            : row.est_shipping_date;
          return selectedRow.value
            ? formatDashboardEstShippingDate(dateVal, true)
            : formatDashboardEstShippingDate(dateVal, false);
        },
      },
      canViewMerchant.value && {
        key: '',
        label: 'Merchant',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) =>
            row.billing_company_name
          ? `${row.order.company_name} / ${row.billing_company_name}`
          : row.order.company_name
      },
      {
        key: '',
        label: 'Customer Reference',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) => row.order.customer_reference_no ?? 'N/A',
      },
      canViewFactory.value && {
        key: 'factory_name',
        label: 'Factory',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const factoryName = row?.factory_name ?? 'N/A';
          if (openDetails.value || factoryName.length > 20) {
            // Show first three letters with tooltip
            return h(
              Tooltip,
              {
                placement: 'top',
                content: factoryName,
              },
              {
                trigger: () =>
                  h(
                    'span',
                    { class: 'whitespace-nowrap font-semibold uppercase' },
                    factoryName.slice(0, 3)
                  ),
              }
            );
          }
          // Show full name if details are closed
          return h('span', { class: 'whitespace-nowrap font-semibold' }, factoryName);
        },
      },
      canViewPaymentStatus.value && {
        key: '',
        label: 'Invoice Status',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300 capitalize' : 'capitalize';
        },
        renderer: (val: any, row: DashboardItem) => {
          const rawComposite = row.invoice_composite_status;
          const composite = rawComposite && String(rawComposite).trim() !== '';
          if (composite) {
            const label = formatInvoiceStatusLabel(String(rawComposite));
            return h('span', { class: invoiceCompositeStatusPillClass(String(rawComposite)) }, label);
          }
          return h('span', { class: invoiceCompositeStatusPillClass('not_invoiced') }, 'Not invoiced');
        },
      },
      {
        key: 'items_total_quantity',
        label: 'Qty',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => row.items_total_quantity ?? 'N/A',
      },
      {
        key: 'status',
        label: 'Status',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const mark_dqc=row.order?.mark_dqc;
          const dqc_user_name = row.order?.dqc_user_name
          const orderId = row.order?.id;
          const rowId = row?.id;


          const hasReadPermission = can('read-dqc-status');
          const hasWritePermission = can('write-dqc-status');

         const dqcCheckbox = h(
            Tooltip,
            {
              placement: 'right',
              content: (mark_dqc && dqc_user_name)
                    ? `DQC (${dqc_user_name}) approved on ` + formatTimestampWithBrowserTimezone(row?.order?.dqc_marked_at)
                    : mark_dqc
                      ? `DQC approved on ` + formatTimestampWithBrowserTimezone(row?.order?.dqc_marked_at)
                      : hasWritePermission
                        ? 'Click to mark DQC approved'
                        : 'View only (no permission to modify)',
            },
            {
              trigger: () =>
                h(
                  'label',
                  {
                    class: [
                      'flex items-center justify-center text-xs font-medium rounded-xl select-none transition-all duration-150',
                      mark_dqc
                        ? 'border-green-500 text-green-700'
                        : 'border-black-700 text-black-700',
                      hasWritePermission ? 'cursor-pointer' : 'cursor-not-allowed',
                    ],
                    onClick: (e: Event) => e.stopPropagation(), // prevent row click
                  },
                  [
                    h('input', {
                      type: 'checkbox',
                      checked: mark_dqc,
                      disabled: !hasWritePermission || isSidepaneLoading.value,

                    class: [
                      'custom-dqc-checkbox w-4 h-4 rounded-md  border-2 border-black-700 transition-all duration-150 relative',
                      mark_dqc
                        ? 'border-green-500 text-green-700'
                        : 'border-black-700 text-black-700',
                      hasWritePermission ? 'cursor-pointer' : 'cursor-not-allowed',
                    ],
                      onChange: (event: Event) =>
                        hasWritePermission ? toggleDQC(event, orderId,rowId) : null,
                    }),
                  ]
                ),
            }
          );


          const showDropdown =
            row.status === 'order_approve' &&
            !row.factory_id &&
            can('assign-factory-to-shareable-orders');

          const handleClick = async (e) => {
            e.stopPropagation();
            if (!showDropdown) return;

            try {
              const orderData = await store.getOrderDetail(row.order_id, row.id);
              const factory_products = orderData.items?.[0]?.factory_products ?? [];
              const { factory_products: _, ...rest } = row;
              assignFactoryModal.value = true;
              selectedOrderItem.value = {
                ...rest,
                factory_products: factory_products.map((prod) => ({
                  ...prod,
                  selected_factory: prod.selected_factory ?? null,
                })),
              };
            } catch (error) {
              logError('Failed to load order details', error);
            }
          };
        const rowComplaints = getComplaintsArray(row.order);
        const complaintPill = rowComplaints.length
          ? h(ComplaintStatusPill, {
              complaints: rowComplaints,
              clickable: true,
              onPillClick: () => handleComplaintPillClick(row),
            })
          : null;
        const rejectedFlag = isMyOrdersRejected(row);

        return h(
          'div',
          {
            class: 'flex items-center justify-center gap-2',
          },
          [
            h(
              'button',
              {
                tabindex: '-1',
                onClick: handleClick,
                class: `${rejectedFlag ? 'mo-rejected-pill' : row.status} w-max ${
                  openDetails.value ? 'max-w-[8rem] p-1' : 'p-2'
                } mx-auto rounded-[4px] flex justify-between items-center`,
              },
              [
              rejectedFlag
                ? (CustimooOrderFlowStatuses[REJECTED_SAMPLE_STATUS] ?? 'Customer Rejected')
                : (CustimooOrderFlowStatuses[row.status] ?? (row.status === 'on_hold' ? 'On Hold' : row.status)),
                showDropdown &&
                  h(
                    'button',
                    {
                      class: 'px-1',
                      onClick: async () => {
                        try {
                          const orderData = await store.getOrderDetail(
                            row.order_id,
                            row.id
                          );
                          const factory_products =
                            orderData.items?.[0]?.factory_products ?? [];
                          const { factory_products: _, ...rest } = row;
                          assignFactoryModal.value = true;
                          selectedOrderItem.value = {
                            ...rest,
                            factory_products: factory_products.map((prod) => ({
                              ...prod,
                              selected_factory: prod.selected_factory ?? null,
                            })),
                          };
                        } catch (error) {
                          logError('Failed to load order details', error);
                        }
                      },
                    },
                    [h(ChevronDownIcon, { class: 'text-[#9197B3] size-4 ml-1' })]
                  ),
              ].filter(Boolean)
            ),
            complaintPill,
            rejectedFlag ? h('span', { class: 'mo-rejected-bang', title: 'Sample rejected - needs attention' }, '!') : null,
            (hasReadPermission || hasWritePermission) ? h('div', { class: 'ml-2' }, [dqcCheckbox]) : null,
          ].filter(Boolean)
        );
        },
      },
      {
        key: 'days_since_action',
        label: openDetails.value ? 'Since Action' : 'Days Since Action',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        sortable: true,
        sortKey: 'status_updated_at',
        renderer: (val: any, row: DashboardItem) =>
          timeAgo(row.order.updated_at ?? Date.now().toString()),
      },
    ];

     // Conditionally add Amount column if permission is granted
    if (can('view-amount-column')) {
      baseColumns.splice(
        baseColumns.findIndex(col => col.key === 'status'), // Insert before Status column
        0,
        {
          key: 'order_total_price',
          label: 'Amount',
          sortable: false,
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
          },
          renderer: (val: any, row: DashboardItem) => {
            const price = row.order_total_price;
            return (price !== undefined && price !== null)
              ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price)
              : 'N/A';
          }
        }
      );
    }

    if (!openDetails.value) {
      return [
        ...baseColumns,
        canViewShippingAddress.value && {
          key: '',
          label: 'Shipping Address',
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
          },
          sortable: false,
          renderer: (val: any, row: DashboardItem) => {
            if (!row.order.shipping_address) return '';
            return `${row.order.shipping_address.address1 || ''} ${row.order.shipping_address.address2 || ''} ${row.order.shipping_address.city || ''}
                  ${row.order.shipping_address.zip_code || ''} ${row.order.shipping_address.country || ''}`;
          },
        },
        canAssignOrderAdmin.value && {
          key: 'admin',
          label: 'Administrator',
          sortable: false,
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
          },
          renderer: (val: any, row: DashboardItem) => {
            if (!row.factory_id && row.order.order_administrator?.name)
              return row.order.order_administrator.name;
            if (!row.factory_id) return 'N/A';
            const filteredOrderAdmins = orderAdmins.value.filter((admin: OrderAdminUser) => {
              // If admin has "leadorderAdministrator" role, include them without factory_id filtering
              // Format role names by removing spaces for comparison
              const formattedRoleNames = admin.role_names?.map(role =>
                role.replace(/\s+/g, '')
              );
              if (formattedRoleNames?.some(role => role.toLowerCase() === 'leadorderadministrator')) {
                return true;
              }
              // Otherwise, filter by factory_id
              return admin.factory_ids?.includes(row.factory_id);
            });
            return h(
              'div',
              {
                onClick: (e: Event) => e.stopPropagation(),
                class: 'w-full h-full',
              },
              [
                h(Select, {
                  options: filteredOrderAdmins,
                  modelValue: row.order.order_administrator,
                  'track-by': 'id',
                  label: 'name',
                  'onUpdate:modelValue': async (newValue: OrderAdminUser) => {
                    row.order.order_administrator = newValue;
                    const secondaryAdminIds: OrderAdminUser[] = row?.order?.secondary_order_administrators
                      ?.map(item => item.user)
                      .filter(Boolean).map(user => user.id) || [];
                    const payload = {
                      user_id: newValue ? newValue.id : null,
                      order_ids: row.order.id,
                      secondary_administrator_ids: secondaryAdminIds
                    };
                    await store.updateOrderAdmin(payload);

                    await fetchRecords({
                      ...defParams,
                      ...current_filters.value,
                    });
                  },
                  class: 'white-space-nowrap min-w-[200px] max-w-max',
                  placeholder: 'Assign Admin',
                  'append-to-body': true,
                }),
              ]
            );
          },
        },
      ].filter(Boolean);
    }

    return baseColumns;
  });

  // AB#19827 (Zendesk#7813) - add the rejected-row cue (red left edge + tint) to each cell of a
  // My Orders row whose sample was rejected, without touching the shared DataTable or other tabs.
  const decorateRejectedCue = (cols: any[]) =>
    (cols || []).map((col: any, idx: number) => {
      if (!col) return col;
      const baseCellClass = col.cellClass;
      return {
        ...col,
        cellClass: (row: DashboardItem) => {
          const base =
            typeof baseCellClass === 'function' ? baseCellClass(row) || '' : baseCellClass || '';
          if (isMyOrdersRejected(row)) {
            return `${base} mo-rejected-cell${idx === 0 ? ' mo-rejected-cell--first' : ''}`;
          }
          return base;
        },
      };
    });
  const columns = computed(() => decorateRejectedCue(visibleColumns.value));

  function hasAllValues(obj: Record<string, any>): boolean {
    return Object.values(obj).every(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );
  }
  const getQuickFilterData = async (filter: any) => {
    // Only jump back to page 1 when the filter set actually changes (a real
    // user action). When the same filters are merely re-applied — e.g. restored
    // from localStorage on page load/refresh — keep the page the user was on.
    const incomingFilters = filter?.quick_filters ?? {};
    const filtersUnchanged =
      JSON.stringify(incomingFilters) === JSON.stringify(dashboardQuickFilters.value ?? {});
    const pageToUse = filtersUnchanged ? (current_filters.value?.page ?? 1) : 1;
    // Preserve the user's current page size and active search when (re)applying
    // quick filters. Rebuilding from `defParams` alone would reset `per_page` to
    // the default and wipe `search` — the latter clears the search box on refresh
    // while the already-fetched results stay filtered (mismatched UI vs results).
    const savedPerPage = current_filters.value?.per_page ?? defParams.per_page;
    const savedSearch = current_filters.value?.search ?? defParams.search ?? '';
    if (filter && typeof filter === 'object' && hasAllValues(filter)) {
      const { quick_filters } = filter;
      defParams = {
        ...defParams,
        page: pageToUse,
        items_for: currentTab.value ?? 'all_items',
      };
      current_filters.value = { ...defParams, per_page: savedPerPage, search: savedSearch };
      localStorage.setItem(
        'dashboardRequest',
        JSON.stringify({ dashboardRequest: current_filters.value })
      );
      dashboardQuickFilters.value = quick_filters ? { ...quick_filters } : {};
    } else {
      defParams = {
        ...defParams,
        page: pageToUse,
        items_for: currentTab.value ?? 'all_items',
      };
      current_filters.value = { ...defParams, per_page: savedPerPage, search: savedSearch };
      localStorage.setItem(
        'dashboardRequest',
        JSON.stringify({ dashboardRequest: current_filters.value })
      );
      dashboardQuickFilters.value = {};
      selectedUrgentOrderFilter.value =
        filter?.urgent_order !== undefined
          ? filter?.urgent_order
          : selectedUrgentOrderFilter.value;
    }
  };

  watch(
    [() => dashboardStore.queryParams, () => dashboardStore.calendarFilterPayload],
    async ([newParams, newPayload], [oldParams, oldPayload]) => {
      if (currentMode.value === 'calendar') {
        if (newParams !== oldParams || newPayload !== oldPayload) {
          await dashboardStore.fetchCalendarData();
        }
      }
    },
    { immediate: true, deep: true }
  );
  // watch(() => [
  //   currentTab,
  //   selectedFilter,
  //   selectedUrgentOrderFilter,
  //   dashboardQuickFilters,
  //   defParams.sort_by,
  //   defParams.sort_order,
  //   defParams.search,
  //   current_filters.value.page], ([newVal]) => { console.log(newVal) })

  const currentOrderItem: ComputedRef<OrderDetailItem | null> = computed(() => {
    return selectedOrderDetails.value
      ? selectedOrderDetails.value.items.find(
        (item) => item.id === currentRow.value?.id
      )
      : null;
  });

  const currentOrderItemIndex: ComputedRef<number | null> = computed(() => {
    return selectedOrderDetails.value
      ? selectedOrderDetails.value.items.findIndex(
        (item) => item.id === currentRow.value?.id
      )
      : null;
  });

  const handleCellClick = async (
    { row }: { row: DashboardItem; column: Column },
    options?: { fromRestore?: boolean },
  ) => {
    if (!options?.fromRestore) {
      // Bug 20628 (Zendesk #8494) — an explicit row pick makes any pending saved-row restore
      // obsolete. Clearing both the ids and the latch stops a later orders refetch (e.g. the one
      // `fetchOnSave` triggers) from re-targeting the dashboard to a previously handled order while
      // the user is working on this one. `fromRestore` keeps the restore path itself untouched so it
      // still scrolls to the restored row via the `selectedRow` computed.
      savedRowIds.value = null;
      fetchOrder.value = false;
    }
    setPageTitle(`#${row.order.order_no}`);
    // Save only order_id and orderItem id instead of full row
    localStorage.setItem('currentRow', JSON.stringify({ order_id: row.order.id, id: row.id }));
    localStorage.setItem('openDetails', 'true');
    openDetails.value = true;
    currentRow.value = row;
    isSidepaneLoading.value = true;
    sidebarOpen.value = false;
    try {
      const orderData = await store.getOrderDetail(row.order.id, row.id);
      selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(orderData, row.order);
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  // Open the side-pane on the timeline tab and scroll to the latest
  // complaint card after the timeline mounts.
  const handleComplaintPillClick = async (row: DashboardItem) => {
    paneDefaultTab.value = 'timeline';
    localStorage.setItem('activeDetailsTab', 'timeline');
    await handleCellClick({ row, column: { key: 'complaint', label: 'complaint' } });

    const latest = getComplaintsArray(row.order)[0];
    if (!latest) return;
    const complaintId = latest.id ?? latest.ticket_no ?? '';
    if (!complaintId) return;

    nextTick(() => {
      setTimeout(() => {
        const el = document.getElementById(`complaint-card-${complaintId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 250);
    });
  };

  const handleOrderMoved = async () => {
    isSidepaneLoading.value = true;

    try {
      if (currentRow.value) {
        const orderData = await store.getOrderDetail(
          currentRow.value.order.id,
          currentRow.value.id,
        );
        selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(
          orderData,
          currentRow.value.order
        );
      }

      await fetchOnSave(true, true);
    } catch (error) {
      logError('Error refetching order details:', error);
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  const handleOrderUpdated = async () => {
    isSidepaneLoading.value = true;

    try {
      if (currentRow.value) {
        const orderData = await store.getOrderDetail(
          currentRow.value.order.id,
          currentRow.value.id,
        );
        selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(
          orderData,
          currentRow.value.order
        );
      }

      await fetchOnSave(true, true);
    } catch (error) {
      logError('Error refetching order details:', error);
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  const openInternalNoteModal = (row) => {
    internalNoteRow.value = row;
    internalNoteModal.value = true;
  };

  const handleInternalNoteClose = async () => {
    internalNoteModal.value = false;
    internalNoteRow.value = null;
    showDashboardActions.value = false;
    await fetchOnSave(true);
  };

  const scrollToCurrentRow = () => {
    const localRow = currentRow.value ?? previousSelectedRow.value;
    if (!localRow || !datatable.value) return;

    nextTick(() => {
      try {
        const tableElement = datatable.value.$el;
        const tableBody = tableElement?.querySelector('tbody');

        if (!tableBody) return;

        const rows = tableBody.querySelectorAll('tr');
        let targetRow: HTMLElement | null = null;

        rows.forEach((row) => {
          const rowData = row.getAttribute('data-row-id') || row.dataset.rowId;
          if (rowData === localRow.id.toString()) {
            targetRow = row;
          }
        });
        if (targetRow) {
          targetRow.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          targetRow.style.transition = 'background-color 0.3s ease';
          targetRow.style.backgroundColor = '#e3f2fd';
          setTimeout(() => {
            targetRow.style.backgroundColor = '';
          }, 1000);
        }
      } catch (error) {
        console.warn('Error scrolling to selected row:', error);
      }
    });
  };

  const openOrderPriceModal = async () => {
    const orderData = await store.getOrderDetail(currentRow.value.order.id,currentRow.value.id);
    selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(
      orderData,
      currentRow.value?.order ?? null
    );
    priceBreakdownModal.value = true;
    await invalidateOrderActivityLogQueries();
  };

  const onDetailsClose = () => {
    selectedOrderDetails.value = null;
    previousSelectedRow.value = { ...currentRow.value };
    localStorage.removeItem('currentRow')
    currentRow.value = null;
    sidebarOpen.value = true;
    openDetails.value = false;
    localStorage.setItem('openDetails', 'false');
    setPageTitle();
  };
  const detailsTabChange = (tabId: string) => {
    paneDefaultTab.value = tabId;
    localStorage.setItem('activeDetailsTab', tabId ?? 'timeline')
  }
  const handleRowSelected = (row: DashboardItem | null) => {
    // Bug 20628 (Zendesk #8494) — `DashboardActions.handlePriceBreakDownModalCloseEvent` emits
    // `selectRow` with `null` when the Price Breakdown modal closes, and the row-actions host binds
    // this handler directly to that event. Without the guard the dereference below throws.
    if (!row?.order?.id) return;
    // Save only order_id and orderItem id instead of full row
    localStorage.setItem('currentRow', JSON.stringify({ order_id: row.order.id, id: row.id }));
    if (!openDetails.value) {
      localStorage.setItem('openDetails', 'false');
      openDetails.value = false;
    }

    currentActiveRow.value = row;
  }
  const openDashboardActions = async ({ event, row }: { event: MouseEvent; row: any }) => {
    event.preventDefault();

    const { clientX, clientY } = event;

    if (popperInstance) {
      popperInstance.destroy();
      popperInstance = null;
    }

    window.virtualReference = {
      getBoundingClientRect: () => new DOMRect(clientX, clientY, 0, 0),
    };
    showDashboardActions.value = true;

    // Bug 20628 (Zendesk #8494) — same reasoning as `handleCellClick`: right-clicking a row is an
    // explicit pick, so any pending saved-row restore is obsolete and must not fire on a later refetch.
    savedRowIds.value = null;
    fetchOrder.value = false;

    // Save only order_id and orderItem id instead of full row
    localStorage.setItem("currentRow", JSON.stringify({ order_id: row.order.id, id: row.id }));
    currentRow.value = row;

    if (currentRow.value) {
      localStorage.setItem("openDetails", "true");

      if (openDetails.value) {
        sidebarOpen.value = false;
      }

      await nextTick();

      if (dashboardActionsRef.value && window.virtualReference) {
        popperInstance = createPopper(window.virtualReference, dashboardActionsRef.value, {
          placement: "bottom-start",
          strategy: "fixed",
          modifiers: [
            { name: "offset", options: { offset: [0, 6] } },
            { name: "preventOverflow", options: { boundary: "viewport" } },
            { name: "flip", options: { fallbackPlacements: ["top-start", "right-start", "left-start"] } },
          ],
        });
      }

      try {
        const orderData = await store.getOrderDetail(row.order.id, row.id);
        selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(orderData, row.order);
      } finally {
        isSidepaneLoading.value = false;
      }
    } else {
      localStorage.setItem("openDetails", "false");
      openDetails.value = false;
    }
  };


  const handleCloseAction = () => {
    showDashboardActions.value = false;
    if (popperInstance) {
      popperInstance.destroy();
      popperInstance = null;
    }
  };


  // AB#20682: third copy of this logic in admin_v2 — delegates to the shared
  // helper. This one only rendered the non-possible branch, which is why a
  // grep for "Possible reorder" did not surface it.
  const makeReorderMessage = (factory_product: FactoryProduct) =>
    sharedMakeReorderMessage(factory_product as unknown as ReorderFactoryProduct);

  const filterByIncludes = (filterBy: unknown, value: string): boolean => {
    if (filterBy == null) return false;
    if (Array.isArray(filterBy)) return filterBy.includes(value);
    return filterBy === value;
  };

  const toggleDQC = async (event: Event,orderId,rowId) => {

    const target = event.target as HTMLInputElement;
    const intendedStatus = target.checked;
      try {
        // Proceed only if confirmed
        target.checked = intendedStatus;
        const payload = {
          dqc_approved: intendedStatus,
          user_id: currentUser.value?.id,
          name: currentUser.value?.name
        };
        await store.updateOrderDQCStatus(orderId, payload);

        if (
            filterByIncludes(dashboardQuickFilters.value?.filter_by, 'marked_dqc') ||
            filterByIncludes(dashboardQuickFilters.value?.filter_by, 'not_marked_dqc')
          ) {
            await fetchOnSave(true);

        }else{

            const orderData = await store.getOrderDetail(orderId);
            const updated = data.value.items.find(
              row => row.id === rowId
            );
            if (updated && updated.order) {
              updated.order.mark_dqc = orderData.mark_dqc;
              updated.order.dqc_user_name = orderData.dqc_user_name;
              updated.order.dqc_marked_at = orderData.dqc_marked_at;
            }
        }

      } catch (error) {
        console.error('Error updating DQC status:', error);
        target.checked = !intendedStatus;
      } finally {
        // No additional action needed here since we handle state in try/catch
      }
  };

  const exportOrderPrices = async (
    fromDate: string,
    endDate: string,
    exportType: string = 'order_item',
    groupBy: string[] = []
  ): Promise<void> => {

    let defParams: DashboardRequest = {
      items_for: currentTab.value ?? 'all_items',
      sort_by: 'order_no',
      sort_order: 'desc',
      page: 1,
      search: '',
      per_page: 10,
      new_dashboard: true
    };

    const currentDashboardModel: Record<string, DashboardRequest> = JSON.parse(
      localStorage.getItem('dashboardRequest') ?? defParamsString
    );
    const current_filters = ref<DashboardRequest>(currentDashboardModel.dashboardRequest);


    const params: DashboardRequest = {
      ...defParams,
      ...current_filters.value,
      page: null,
      per_page: null
    }

    await store.exportOrderPrices(params, dashboardQuickFilters.value, {
      fromDate: fromDate,
      endDate: endDate,
      exportType: exportType,
      groupBy: groupBy,
    });
  };

  const getShippmentBookingCsv = async () => {
    try {
        isDisabled.value = true;
        const response = await http.get('/file/shippment-csv');

        const downloadUrl = response.data.result.url;

        if (!downloadUrl) {
            isDisabled.value = false;
            showToast('File URL not found', 'error');
            return;
        }

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'shipment_report.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        isDisabled.value = false;

    } catch (error) {
        isDisabled.value = false;
        showToast('Shipments file does not exist', 'error');
    }
  };

  // watch([selectedOrderDetails], () => {
  //   if (currentRow.value) {
  //     scrollToCurrentRow();
  //   }
  // });
  return {
    appURL,
    dashboardTabs,
    permissionLoaded,
    datatable,
    currentTab,
    createManualOrder,
    assignFactoryModal,
    selectedOrderDetails,
    isSidepaneLoading,
    priceBreakdownModal,
    selectedOrderItem,
    internalNoteModal,
    internalNoteRow,
    currentRow,
    current_filters,
    filteredOrderDetailTabs,
    filteredModeTabs,
    orderOptions,
    tableData,
    totalItems,
    columns,
    currentOrderItem,
    currentOrderItemIndex,
    error,
    isFetching,
    isPending,
    currentMode,
    dashboardStore,
    sidebarOpen,
    dashboardData,
    paneDefaultTab,
    taskSearchQuery,
    isDisabled,
    getStatusColor,
    handlePriceBreakDownModalCloseEvent,
    onOrderSearch,
    onModeTabChange,
    handleViewChanged,
    onCalendarFilterChange,
    factoryModalClose,
    fetchRecords,
    onTabChange,
    fetchOnSave,
    getQuickFilterData,
    handleCellClick,
    handleOrderMoved,
    handleOrderUpdated,
    handleInternalNoteClose,
    openOrderPriceModal,
    onDetailsClose,
    detailsTabChange,
    handleRowSelected,
    currentActiveRow,
    openDashboardActions,
    showDashboardActions,
    dashboardActionsRef,
    openDetails,
    handleCloseAction,
    selectedRow,
    makeReorderMessage,
    exportOrderPrices,
    getShippmentBookingCsv,
  };
};
